// JSBridge Client - Communication with Electron Shell

import type {
  BridgeRequest,
  BridgeResponse,
  BridgeAction,
  BridgeEventType,
  BridgeCapability,
  PrinterListResponse,
  PrinterPrintPayload,
  PrinterPrintResponse,
  PrinterTestResponse,
  PrinterGetSettingsResponse,
  PrinterSaveSettingsPayload,
  PrinterSaveSettingsResponse,
  CounterStartPayload,
  CounterStartResponse,
  CounterStopResponse,
  CounterStatusResponse,
  CounterTestPayload,
  CounterTestResponse,
  CounterGetSettingsResponse,
  CounterSaveSettingsPayload,
  CounterSaveSettingsResponse,
  SystemInfoResponse,
  WindowOpenCustomerDisplayResponse,
  WindowCloseCustomerDisplayResponse,
  WindowIsCustomerDisplayOpenResponse,
} from '@abcpay/shared'

export class BridgeError extends Error {
  constructor(public code: string, message: string) {
    super(message)
    this.name = 'BridgeError'
  }
}

export class BridgeNotAvailableError extends BridgeError {
  constructor(action: string) {
    super('BRIDGE_NOT_AVAILABLE', `Cannot execute ${action}: Not running in Electron shell`)
  }
}

class BridgeClient {
  private static instance: BridgeClient
  private eventListeners: Map<string, Set<(data: unknown) => void>> = new Map()

  private constructor() {}

  static getInstance(): BridgeClient {
    if (!BridgeClient.instance) {
      BridgeClient.instance = new BridgeClient()
    }
    return BridgeClient.instance
  }

  /**
   * Check if running inside Electron shell with bridge available
   */
  get isElectron(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.abcPayBridge !== undefined &&
      window.abcPayBridge.isElectron === true
    )
  }

  /**
   * Get current platform (or 'browser' if not in Electron)
   */
  get platform(): NodeJS.Platform | 'browser' {
    if (!this.isElectron) return 'browser'
    // Platform will be fetched via system:info call
    return 'darwin' // Default, will be updated
  }

  /**
   * Get bridge version (or null if not in Electron)
   */
  get version(): string | null {
    if (!this.isElectron) return null
    return window.abcPayBridge!.version
  }

  /**
   * Get shell capabilities
   */
  getCapabilities(): BridgeCapability[] {
    if (!this.isElectron) return []
    return window.abcPayBridge!.getCapabilities()
  }

  /**
   * Send a request to the shell and wait for response
   */
  async send<Req, Res>(action: BridgeAction, payload: Req): Promise<Res> {
    if (!this.isElectron) {
      throw new BridgeNotAvailableError(action)
    }

    const request: BridgeRequest<Req> = {
      id: crypto.randomUUID(),
      type: 'request',
      action,
      payload,
      timestamp: Date.now(),
    }

    const response: BridgeResponse<Res> = await window.abcPayBridge!.send(request)

    if (!response.success) {
      throw new BridgeError(
        response.error?.code || 'UNKNOWN',
        response.error?.message || 'Bridge operation failed'
      )
    }

    return response.data!
  }

  /**
   * Subscribe to shell events
   * Returns an unsubscribe function
   */
  on(event: BridgeEventType, callback: (data: unknown) => void): () => void {
    if (!this.isElectron) return () => {}

    // Track listeners locally for potential cleanup
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(callback)

    // Subscribe via bridge
    const unsubscribe = window.abcPayBridge!.on(event, callback)

    // Return combined unsubscribe
    return () => {
      this.eventListeners.get(event)?.delete(callback)
      unsubscribe()
    }
  }

  // ===== Printer Operations =====

  async listPrinters(): Promise<PrinterListResponse> {
    return this.send<{}, PrinterListResponse>('printer:list', {})
  }

  async printReceipt(payload: PrinterPrintPayload): Promise<PrinterPrintResponse> {
    return this.send<PrinterPrintPayload, PrinterPrintResponse>('printer:print', payload)
  }

  // 웹에서 완성한 ESC/POS 바이트를 그대로 출력 (신버전 셸 전용)
  async printReceiptRaw(payload: { dataBase64: string }): Promise<PrinterPrintResponse> {
    return this.send<{ dataBase64: string }, PrinterPrintResponse>('printer:printRaw', payload)
  }

  async testPrinter(): Promise<PrinterTestResponse> {
    return this.send<{}, PrinterTestResponse>('printer:test', {})
  }

  async getPrinterSettings(): Promise<PrinterGetSettingsResponse> {
    return this.send<{}, PrinterGetSettingsResponse>('printer:getSettings', {})
  }

  async savePrinterSettings(payload: PrinterSaveSettingsPayload): Promise<PrinterSaveSettingsResponse> {
    return this.send<PrinterSaveSettingsPayload, PrinterSaveSettingsResponse>('printer:saveSettings', payload)
  }

  // ===== Counter Operations =====

  async startCounter(payload: CounterStartPayload): Promise<CounterStartResponse> {
    return this.send<CounterStartPayload, CounterStartResponse>('counter:start', payload)
  }

  async stopCounter(): Promise<CounterStopResponse> {
    return this.send<{}, CounterStopResponse>('counter:stop', {})
  }

  async getCounterStatus(): Promise<CounterStatusResponse> {
    return this.send<{}, CounterStatusResponse>('counter:status', {})
  }

  async testCounter(payload: CounterTestPayload): Promise<CounterTestResponse> {
    return this.send<CounterTestPayload, CounterTestResponse>('counter:test', payload)
  }

  async getCounterSettings(): Promise<CounterGetSettingsResponse> {
    return this.send<{}, CounterGetSettingsResponse>('counter:getSettings', {})
  }

  async saveCounterSettings(payload: CounterSaveSettingsPayload): Promise<CounterSaveSettingsResponse> {
    return this.send<CounterSaveSettingsPayload, CounterSaveSettingsResponse>('counter:saveSettings', payload)
  }

  // ===== System Operations =====

  async getSystemInfo(): Promise<SystemInfoResponse> {
    return this.send<{}, SystemInfoResponse>('system:info', {})
  }

  // ===== Window Operations =====

  async openCustomerDisplay(): Promise<WindowOpenCustomerDisplayResponse> {
    return this.send<{}, WindowOpenCustomerDisplayResponse>('window:openCustomerDisplay', {})
  }

  async closeCustomerDisplay(): Promise<WindowCloseCustomerDisplayResponse> {
    return this.send<{}, WindowCloseCustomerDisplayResponse>('window:closeCustomerDisplay', {})
  }

  async isCustomerDisplayOpen(): Promise<WindowIsCustomerDisplayOpenResponse> {
    return this.send<{}, WindowIsCustomerDisplayOpenResponse>('window:isCustomerDisplayOpen', {})
  }
}

// Singleton export
export const bridge = BridgeClient.getInstance()

// Re-export types
export type { BridgeAction, BridgeEventType, BridgeCapability }
